import React from "react";
import { FC } from "react";
import { ComponentProps } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core";
import { Theme } from "@material-ui/core";
import { TypographyProps } from "@material-ui/core";
import { darken } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import { endOfTitle } from "../../constants/constants";
import FontIcon from "../FontIcon";

interface PageHeaderProps extends ComponentProps<any> {
  title: string;
  subtitle?: string;
  titleProps?: Partial<TypographyProps>;
  subtitleProps?: Partial<TypographyProps>;
  disableBackButton?: boolean;
  endAdornment?: React.ReactNode;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: "flex",
    flexDirection: "row",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  backButton: {
    marginLeft: -10,
    marginRight: 10,
    color: theme.palette.secondary.light,
    "&:hover": {
      background: "transparent",
      color: darken(theme.palette.secondary.light, 0.1),
    },
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 14,
  },
  container: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
}));

const PageHeader: FC<PageHeaderProps> = ({
  title,
  subtitle,
  titleProps,
  subtitleProps,
  disableBackButton,
  className,
  endAdornment,
}) => {
  const classes = useStyles();
  const history = useHistory();

  return (
    <div className={clsx(className, classes.root)}>
      <Helmet>
        <title>
          {title}
          {endOfTitle}
        </title>
        <meta name="description" content={subtitle} />
      </Helmet>
      {!disableBackButton && (
        <div className={classes.buttonContainer}>
          <IconButton
            disableRipple={true}
            onClick={() => history.goBack()}
            className={classes.backButton}
          >
            <FontIcon name={"back"} />
          </IconButton>
        </div>
      )}
      <div className={classes.container}>
        <Typography
          {...{
            className: classes.title,
            component: "h1",
            ...titleProps,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            {...{
              className: classes.subtitle,
              component: "h2",
              ...subtitleProps,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </div>
      {endAdornment && (
        <div className={classes.buttonContainer}>{endAdornment}</div>
      )}
    </div>
  );
};

export default React.memo(PageHeader);
